import { useEffect, useState } from "react";
import { Landmark, Loader2, Plus, ExternalLink, ShieldCheck, RefreshCw, AlertTriangle, CheckCircle2, FileText, ChevronRight } from "lucide-react";
import api, { fmtINR, formatDetail, shortDate } from "@/lib/api";
import Modal from "@/components/Modal";

const STATUS_PILL = {
    PENDING: "pill-pending",
    ACTIVE: "pill-success",
    REJECTED: "pill-failed",
    REVOKED: "pill-failed",
    EXPIRED: "pill-muted",
};

/** Partner-facing Account Aggregator (AA) page.
 *  Lets the partner kick off RBI-compliant bank-statement consents and
 *  view fetched statements per-customer.
 */
export default function PartnerAA() {
    const [status, setStatus] = useState(null);
    const [consents, setConsents] = useState([]);
    const [statements, setStatements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [selected, setSelected] = useState(null);
    const [msg, setMsg] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const s = await api.get("/partner/aa/status");
            setStatus(s.data);
            // Only fetch consents when feature is enabled — server returns
            // 403 otherwise and we don't want red console errors.
            if (s.data?.enabled) {
                const c = await api.get("/partner/aa/consents");
                setConsents(c.data.items || []);
            } else {
                setConsents([]);
            }
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const loadStatements = async (consentId) => {
        const r = await api.get(`/partner/aa/statements${consentId ? `?consent_id=${consentId}` : ""}`);
        setStatements(r.data.items || []);
    };

    const stubApprove = async (cid) => {
        await api.post(`/partner/aa/consents/${cid}/stub-approve`);
        load();
    };
    const fetchData = async (cid) => {
        setMsg("");
        try {
            const r = await api.post("/partner/aa/fetch", { consent_id: cid, days: 30 });
            setMsg(`✅ Fetched ${r.data.records || 0} account record(s).`);
            load();
            if (selected === cid) loadStatements(cid);
        } catch (e) {
            setMsg(formatDetail(e?.response?.data?.detail) || e.message);
        }
        setTimeout(() => setMsg(""), 4000);
    };
    const viewStatements = (cid) => {
        setSelected(cid);
        loadStatements(cid);
    };

    return (
        <div className="space-y-5 max-w-5xl">
            <div>
                <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                    <Landmark className="w-6 h-6 text-amber-700" /> Bank Aggregator (AA)
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    RBI-compliant bank statement fetch via Setu Account Aggregator. Customer authorises one-time, your team auto-pulls statements for reconciliation.
                </p>
            </div>

            {status && !status.enabled && (
                <div className="card p-8 text-center bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200" data-testid="aa-upgrade-cta">
                    <Landmark className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                    <div className="text-lg font-semibold text-slate-900">Bank Aggregator add-on</div>
                    <div className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
                        {status.message}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-4">
                        Once enabled, you can request RBI-compliant consent from any customer and pull bank statements with one click.
                    </div>
                </div>
            )}

            {status && status.enabled && (
                <div className={`card p-4 flex items-start gap-3 ${status.stub_mode ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
                    {status.stub_mode
                        ? <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                        : <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />}
                    <div className="text-sm">
                        <div className="font-semibold">{status.provider} · {status.stub_mode ? "Sandbox stub" : "Live"}</div>
                        <div className="text-slate-600 mt-0.5">{status.message}</div>
                    </div>
                </div>
            )}

            {msg && (
                <div className="card p-3 text-sm bg-blue-50 border-blue-200 text-blue-800" data-testid="aa-message">
                    {msg}
                </div>
            )}

            {status?.enabled && (
                <div className="card p-5">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> Consents</h2>
                    <button data-testid="aa-new-consent" onClick={() => setCreating(true)}
                            className="btn-primary px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5">
                        <Plus className="w-3.5 h-3.5" /> New consent
                    </button>
                </div>
                {loading ? (
                    <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline-block text-amber-700" /></div>
                ) : consents.length === 0 ? (
                    <div className="text-center text-sm text-slate-400 py-8">
                        No consents yet. Click &quot;New consent&quot; to authorise a customer&apos;s bank.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {consents.map((c) => (
                            <ConsentRow key={c.consent_id} c={c}
                                          onStubApprove={() => stubApprove(c.consent_id)}
                                          onFetch={() => fetchData(c.consent_id)}
                                          onView={() => viewStatements(c.consent_id)}
                                          stub={status?.stub_mode}
                                          selected={selected === c.consent_id} />
                        ))}
                    </div>
                )}
                </div>
            )}

            {status?.enabled && selected && (
                <StatementsPanel statements={statements} consentId={selected}
                                  onClose={() => { setSelected(null); setStatements([]); }} />
            )}

            {status?.enabled && creating && (
                <NewConsentModal onClose={() => setCreating(false)}
                                  onCreated={() => { setCreating(false); load(); }} />
            )}
        </div>
    );
}

function ConsentRow({ c, onStubApprove, onFetch, onView, stub, selected }) {
    return (
        <div data-testid={`aa-consent-${c.consent_id}`}
             className={`border rounded-md p-3 flex items-center justify-between gap-3 ${
                 selected ? "border-amber-400 bg-amber-50/40" : "border-slate-200 hover:bg-slate-50/60"
             }`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{c.customer_name || "—"}</span>
                    <span className="font-mono text-[11px] text-slate-500">{c.customer_mobile}</span>
                    <span className={`pill ${STATUS_PILL[c.status] || "pill-muted"}`}>{c.status}</span>
                    {c.is_stub && <span className="pill pill-info">stub</span>}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span>FI types: <span className="font-mono">{(c.fi_types || []).join(", ")}</span></span>
                    <span>· {c.duration_months} mo</span>
                    <span>· created {shortDate(c.created_at)}</span>
                    {c.linked_fip && <span>· FIP: <span className="font-mono">{c.linked_fip}</span></span>}
                </div>
                {c.redirect_url && c.status === "PENDING" && (
                    <a href={c.redirect_url} target="_blank" rel="noreferrer"
                       data-testid={`aa-redirect-${c.consent_id}`}
                       className="text-[11px] text-blue-700 hover:underline mt-1 inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Customer approval link
                    </a>
                )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                {c.status === "PENDING" && stub && (
                    <button data-testid={`aa-stub-approve-${c.consent_id}`} onClick={onStubApprove}
                            title="Sandbox: approve immediately"
                            className="btn-secondary border-amber-200 text-amber-700 hover:bg-amber-50 px-2.5 py-1 rounded-md text-xs flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Stub approve
                    </button>
                )}
                {c.status === "ACTIVE" && (
                    <>
                        <button data-testid={`aa-fetch-${c.consent_id}`} onClick={onFetch}
                                title="Fetch latest 30 days of statements"
                                className="btn-secondary text-emerald-700 hover:bg-emerald-50 px-2.5 py-1 rounded-md text-xs flex items-center gap-1">
                            <RefreshCw className="w-3.5 h-3.5" /> Fetch
                        </button>
                        <button data-testid={`aa-view-${c.consent_id}`} onClick={onView}
                                className="text-slate-600 hover:bg-slate-100 px-2.5 py-1 rounded-md text-xs flex items-center gap-1">
                            View <ChevronRight className="w-3 h-3" />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

function StatementsPanel({ statements, consentId, onClose }) {
    return (
        <div className="card p-5" data-testid="aa-statements-panel">
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold">Fetched statements · <span className="font-mono text-xs text-slate-500">{consentId}</span></h2>
                <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-700">Close</button>
            </div>
            {statements.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-6">No statements pulled yet for this consent.</div>
            ) : (
                <div className="space-y-3">
                    {statements.map((s, idx) => {
                        const fi = s.fi_data || {};
                        const summary = fi.Summary || {};
                        const txns = fi.Transactions?.Transaction || [];
                        return (
                            <div key={`${s.fip}-${s.masked_account}-${idx}`} className="border border-slate-200 rounded-md p-3">
                                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                    <div>
                                        <div className="font-medium text-sm">{s.fip} · <span className="font-mono">{s.masked_account}</span></div>
                                        <div className="text-[11px] text-slate-500">Fetched {shortDate(s.fetch_timestamp)}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] text-slate-400 uppercase tracking-wider">Current bal</div>
                                        <div className="font-mono font-semibold text-sm">{summary.currentBalance != null ? fmtINR(summary.currentBalance) : "—"}</div>
                                    </div>
                                </div>
                                {txns.length > 0 && (
                                    <div className="overflow-x-auto -mx-3">
                                        <table className="w-full text-xs" data-testid={`aa-txns-${idx}`}>
                                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                                <tr>
                                                    <th className="px-3 py-1.5 text-left">Date</th>
                                                    <th className="px-3 py-1.5 text-left">Narration</th>
                                                    <th className="px-3 py-1.5 text-right">Amount</th>
                                                    <th className="px-3 py-1.5 text-right">Balance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {txns.slice(0, 15).map((t, i) => (
                                                    // Composite key — AA txns lack a stable id, so
                                                    // (date+amount+narration) gives near-uniqueness
                                                    // while still tolerating duplicate timestamps.
                                                    <tr key={`${t.valueDate}-${t.amount}-${i}`} className="border-t border-slate-100">
                                                        <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500">{shortDate(t.valueDate)}</td>
                                                        <td className="px-3 py-1.5 truncate max-w-[280px]">{t.narration}</td>
                                                        <td className={`px-3 py-1.5 text-right font-mono font-medium ${t.type === "CREDIT" ? "text-emerald-700" : "text-rose-700"}`}>
                                                            {t.type === "CREDIT" ? "+" : "−"}{fmtINR(t.amount)}
                                                        </td>
                                                        <td className="px-3 py-1.5 text-right font-mono text-slate-500">{t.currentBalance != null ? fmtINR(t.currentBalance) : "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function NewConsentModal({ onClose, onCreated }) {
    const [f, setF] = useState({
        customer_mobile: "",
        customer_name: "",
        duration_months: 6,
        purpose: "Bank reconciliation for payments",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            await api.post("/partner/aa/consents", f);
            onCreated();
        } catch (e2) {
            setErr(formatDetail(e2?.response?.data?.detail) || e2.message);
        } finally { setBusy(false); }
    };
    return (
        <Modal testId="aa-new-consent-modal" onClose={onClose}
                title="New AA consent — bank statement fetch" size="md">
            <form onSubmit={submit} className="space-y-3 text-sm">
                <div>
                    <label className="label">Customer mobile <span className="text-rose-500">*</span></label>
                    <input data-testid="aa-mobile" required minLength={10} maxLength={15}
                            pattern="[0-9+ -]+" className="input font-mono"
                            placeholder="9876543210"
                            value={f.customer_mobile}
                            onChange={(e) => setF({ ...f, customer_mobile: e.target.value })} />
                    <div className="text-[10px] text-slate-400 mt-0.5">Customer&apos;s bank-registered mobile number</div>
                </div>
                <div>
                    <label className="label">Customer name <span className="text-slate-400 text-[10px]">(optional)</span></label>
                    <input data-testid="aa-name" maxLength={120} className="input"
                            value={f.customer_name}
                            onChange={(e) => setF({ ...f, customer_name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="label">Duration (months)</label>
                        <input data-testid="aa-duration" type="number" min={1} max={24}
                                className="input"
                                value={f.duration_months}
                                onChange={(e) => setF({ ...f, duration_months: parseInt(e.target.value || 6, 10) })} />
                    </div>
                </div>
                <div>
                    <label className="label">Purpose</label>
                    <input data-testid="aa-purpose" maxLength={200} className="input"
                            value={f.purpose}
                            onChange={(e) => setF({ ...f, purpose: e.target.value })} />
                </div>
                {err && <div className="text-rose-600 bg-rose-50 border border-rose-200 rounded p-2 text-xs">{err}</div>}
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md">Cancel</button>
                    <button data-testid="aa-create-submit" type="submit" disabled={busy}
                            className="btn-primary px-4 py-2 rounded-md flex items-center gap-2">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Create consent
                    </button>
                </div>
            </form>
        </Modal>
    );
}
